import { cn } from 'cn';
import { type ReactNode, useId } from 'react';

import { isFieldRequired, unique } from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';

import { MultiSelectSites, type Option } from '../SelectServerDriven';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

// Define a Site type based on the expected structure; TODO: this should come as response type from the request.
type Site = {
  id: string;
  name: string;
};

type P = {
  readonly allowedSiteIds?: string[];
  readonly classNameErrors?: string;
  readonly filterActive?: boolean | null;
  readonly formatOptionLabel?: (option: Option<Site>) => ReactNode;
  readonly hint?: string;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<Site>) => boolean;
  readonly label?: string;
  readonly placeholder?: string;
  readonly testId?: string;
} & ComponentStyling;

export const MultiSelectSitesField = ({
  allowedSiteIds,
  className,
  classNameErrors,
  filterActive = true,
  formatOptionLabel,
  hint,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  label,
  placeholder,
  style,
  testId,
}: P) => {
  const field = useFieldContext();

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const hasErrors = Boolean(field.state.meta.errors?.length);

  const id = useId();

  return (
    <div className={cn('relative', className)} style={style}>
      {Boolean(label) && (
        <InputLabel hint={hint} htmlFor={id} isRequired={isRequired}>
          {label}
        </InputLabel>
      )}
      <MultiSelectSites
        data-testid={testId}
        allowedSiteIds={allowedSiteIds}
        filterActive={filterActive}
        formatOptionLabel={formatOptionLabel}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMultiSelect={isMultiSelect}
        isRemovableValue={isRemovableValue}
        placeholder={placeholder}
        value={(field.state.value ?? []) as string | string[]}
        onChange={(sites) => {
          const isArray = Array.isArray(sites);
          if (isArray) {
            field.handleChange(sites.map(({ id }) => id));
          } else {
            field.handleChange(sites?.id ?? '');
          }
        }}
      />
      {hasErrors ? (
        <ValidationError className={classNameErrors}>
          {unique(field.state.meta.errors.map(({ message }) => message)).join(
            ', ',
          )}
        </ValidationError>
      ) : null}
    </div>
  );
};
